import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TenantLocalService } from 'src/app/core/services/api/tenantlocal.service';
import { fi } from 'date-fns/locale';
import { AuthService } from 'src/app/core/services/auth/auth.service';

const alwaysVisibleLinks = ['all', 'kanban'];
enum LINK_TYPE {
    link = 'link',
    tree = 'tree'
}

export interface SidebarRoute {
    label: string;
    slug: string[];
    link?: string;
}

export interface SidebarLink extends SidebarRoute {
    type: LINK_TYPE.link;
    icon?: string;
}

export type SidebarItem = SidebarLink | SidebarTreeLink;
export interface SidebarTreeLink extends Omit<SidebarRoute, 'link'> {
    type: LINK_TYPE.tree;
    icon?: string;
    tree: SidebarItem[];
    isOpen?: boolean;
}

export const sidebarLinks: SidebarItem[] = [
  {
    type: LINK_TYPE.link,
    icon: 'home',
    label: 'Inicio',
    slug: ['all'],
    link: 'home'
  },
  {
    type: LINK_TYPE.tree,
    icon: 'web',
    label: 'Administrador',
    slug: ['user.list', 'role.list', 'tipo-cambio.list','clipping.list', 'admin.valorizaciones.list','admin.configuration','admin.subscription', 'admin.contacts.list'],
    tree: [
      { type: LINK_TYPE.link, label: 'Usuarios',  slug: ['user.list'], link: 'admin/users' },
      { type: LINK_TYPE.link, label: 'Roles', slug: ['role.list'], link: 'admin/roles' },
      { type: LINK_TYPE.link, label: 'Tipo de cambio', slug:['tipo-cambio.list'], link: 'admin/rate' },
      { type: LINK_TYPE.link, label: 'Clipping', slug:['clipping.list'], link: 'admin/clipping' },
      {
        type: LINK_TYPE.tree,
        label: 'Importar/Exportar',
        slug:['admin.valorizaciones.list', 'admin.contacts.list'],
        tree: [
          { type: LINK_TYPE.link, label: 'Contactos', slug:['admin.contacts.list'], link: 'admin/import/contacts' },
          { type: LINK_TYPE.link, label: 'Valorizaciones', slug: ['admin.valorizaciones.list'], link: 'admin/import/valorizations' },
        ] 
      },
      { type: LINK_TYPE.link, label: 'Actividad de usuarios', slug: ['admin.activity'], link: 'admin/user-activity/list' },
      { type: LINK_TYPE.link, label: 'Configuración', slug: ['admin.configuration'], link: 'admin/configuration' },
      /* { type: LINK_TYPE.link, label: 'Mi suscripción', slug: ['admin.subscription'], link: 'admin/my-subscription' } */
    ]
  },
  {
    type: LINK_TYPE.tree,
    icon: 'cloud',
    label: 'Gestión',
    slug: ['persona.list', 'medio.list', 'cliente.list', 'campaign.list', 'nota-prensa.list', 'plan-medio.list'],
    tree: [
      { type: LINK_TYPE.link, label: 'Personas', slug: ['persona.list'], link: 'database/people' },
      { type: LINK_TYPE.link, label: 'Medios', slug: ['medio.list'], link: 'database/media' },
      { type: LINK_TYPE.link, label: 'Clientes', slug: ['cliente.list'], link: 'database/customers' },
      { type: LINK_TYPE.link, label: 'Campañas', slug: ['campaign.list'], link: 'database/campaigns' },
      { type: LINK_TYPE.link, label: 'Plan de medios', slug: ['plan-medio.list'], link: 'database/media-plan' },
      { type: LINK_TYPE.link, label: 'Notas de prensa', slug: ['nota-prensa.list'], link: 'database/press-release' },
      { type: LINK_TYPE.link, label: 'Grupo de campañas', slug: ['group-campaigns.list'], link: 'database/group-campaigns' }
    ]
  },
  {
    type: LINK_TYPE.tree,
    icon: 'description',
    label: 'Reportes',
    slug: ['reporte.generar-reporte', 'reporte.resumen-impactos', 'reporte.grafico-impactos', 'reporte.estado'],
    tree: [
      { type: LINK_TYPE.link, label: 'Generar reporte', link: 'reports/generate-report', slug: ['reporte.generar-reporte'] },
      { type: LINK_TYPE.link, label: 'Resumen por impactos', link: 'reports/platform-impacts', slug: ['reporte.resumen-impactos'] },
      { type: LINK_TYPE.link, label: 'Grafico impactos por plataformas', link: 'reports/graph-impacts-by-platforms', slug: ['reporte.grafico-impactos'] },
      { type: LINK_TYPE.link, label: 'Estado por campaña o vocero', link: 'reports/status-by-campaign-or-spokesperson', slug: ['reporte.estado'] },
    ]
  },
  /*{
    type: LINK_TYPE.tree,
    icon: 'storage',
    label: 'Sistema experto',
    slug: ['sistema-experto.casos.list', 'sistema-experto.analisis'],
    tree: [
      { type: LINK_TYPE.link, label: 'Análisis estratégico', link: 'expert-system/analisis', slug: ['sistema-experto.analisis'] },
      { type: LINK_TYPE.link, label: 'Envío de casos', link: 'expert-system/list', slug: ['sistema-experto.casos.list'] }
    ]
  },*/ //Descansa en paz Sistema experto, volveras mas fuerte y con otro nombre... Lo prometo. (Adrian Fujiki, 2026)
  {
    type: LINK_TYPE.link,
    icon: 'view_column',
    label: 'Kanban',
    slug: ['kanban'],
    link: 'kanban'
  },
  {
    type: LINK_TYPE.link,
    icon: 'build',
    label: 'Mantenimiento',
    slug: ['mantenimiento'],
    link: 'maintenance'
  },
];

@Component({
    selector: 'adp-sidebar-links',
    templateUrl: './sidebar-links.component.html',
    styleUrls: ['./sidebar-links.component.scss']
})
export class SidebarLinksComponent {
    private auth = inject(AuthService);
    private router = inject(Router);
    private tenantLocal = inject(TenantLocalService);

    public links: Array<SidebarLink | SidebarTreeLink> = [];

    constructor() {
        const tenant = this.tenantLocal.tenant;
        this.links = this.filterLinksByPermissions(sidebarLinks, this.auth.isPermitMenu(), tenant?.plan_venta);
    }

    private filterLinksByPermissions(links: Array<SidebarLink | SidebarTreeLink>, permissions: string[], planVenta: any): Array<SidebarLink | SidebarTreeLink> {
        const modulosPermitidos = planVenta?.modulos?.split(',') ?? [];
        return links.map(link => {
            const transformedLink = {...link, label: this.capitalizeFirstLetter(link.label)};
          
            if (transformedLink.type === LINK_TYPE.tree) {
                /*if (transformedLink.label === 'Sistema experto' && !modulosPermitidos.includes('1') || transformedLink.label === 'Kanban' && !modulosPermitidos.includes('3')) return null;

                if (transformedLink.label === 'Sistema experto' && !modulosPermitidos.includes('1')) return null;*/

                const filteredChildren = this.filterLinksByPermissions(transformedLink.tree, permissions, planVenta).filter(item => !(item?.label === 'Clipping' && !modulosPermitidos.includes('2')));
                return filteredChildren.length > 0 ? { ...transformedLink, tree: filteredChildren } : null;
            }
            
            if (transformedLink.type === LINK_TYPE.link) {
                if (transformedLink.label === 'Kanban' && !modulosPermitidos.includes('3')) return null;
            }
          
            return (link.slug.some(s => permissions.includes(s)) || alwaysVisibleLinks.some(av => link.slug.includes(av))) ? transformedLink : null;
        }).filter(link => link !== null) as Array<SidebarLink | SidebarTreeLink>;
    }

    private capitalizeFirstLetter(string: string): string {
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    }

    onAccordionToggle(isExpanded: boolean, route: any) {
        if (!isExpanded && route.tree) {
            for (const item of route.tree) {
                if (item.type === 'tree') {
                    item.isOpen = false;
                }
            }
        }
    }
}